/* eslint-disable react/jsx-one-expression-per-line */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import styled, { keyframes } from "styled-components";
import { CircleHelp, TrendingDown, TrendingUp } from "lucide-react";

const C = {
  bg: "#0b0908",
  panel: "#141009",
  panelHi: "#1c1610",
  border: "#33291c",
  red: "#ff3b30",
  redDeep: "#8f1d16",
  amber: "#ffb000",
  green: "#6fe06a",
  text: "#d8d2c4",
  dim: "#847b6c",
};

const MONO = "ui-monospace, 'Cascadia Mono', 'JetBrains Mono', Menlo, Consolas, monospace";
const CACHE_KEY = "doom-scan-v2";

const STATS = [
  {
    id: "prime_age_epop",
    label: "Prime-Age Employment",
    sub: "Employment-population ratio, ages 25-54",
    unit: "%",
    decimals: 1,
    baseline: 80.7,
    watch: 80.0,
    doom: 78.7,
    direction: "below",
    claim: "Tests: no jobs for anyone",
    sourceLabel: "FRED LNS12300060",
    sourceUrl: "https://fred.stlouisfed.org/series/LNS12300060",
  },
  {
    id: "u3",
    label: "U-3 Unemployment",
    sub: "Headline unemployment rate",
    unit: "%",
    decimals: 1,
    baseline: 4.3,
    watch: 5.0,
    doom: 6.0,
    direction: "above",
    claim: "Tests: displacement velocity",
    sourceLabel: "FRED UNRATE",
    sourceUrl: "https://fred.stlouisfed.org/series/UNRATE",
  },
  {
    id: "unemp_duration",
    label: "Median Unemp. Duration",
    sub: "Median weeks unemployed",
    unit: "wk",
    decimals: 1,
    baseline: 10.4,
    watch: 13,
    doom: 17,
    direction: "above",
    claim: "Tests: reabsorption failing",
    sourceLabel: "FRED UEMPMED",
    sourceUrl: "https://fred.stlouisfed.org/series/UEMPMED",
  },
  {
    id: "labor_share",
    label: "Labor Share Of Income",
    sub: "Nonfarm business sector labor share",
    unit: "%",
    decimals: 1,
    baseline: 55.8,
    watch: 54.5,
    doom: 52.8,
    direction: "below",
    claim: "Tests: the rich eat the gains",
    sourceLabel: "BLS labor productivity",
    sourceUrl: "https://www.bls.gov/productivity/",
  },
  {
    id: "grad_unemp",
    label: "Recent-Grad Unemployment",
    sub: "College graduates ages 22-27",
    unit: "%",
    decimals: 1,
    baseline: 4.8,
    watch: 6.5,
    doom: 8.5,
    direction: "above",
    claim: "Tests: no jobs for juniors",
    sourceLabel: "NY Fed labor market",
    sourceUrl: "https://www.newyorkfed.org/research/college-labor-market",
  },
  {
    id: "grad_underemp",
    label: "Recent-Grad Underemployment",
    sub: "Recent grads in jobs not requiring a degree",
    unit: "%",
    decimals: 1,
    baseline: 41.0,
    watch: 45,
    doom: 50,
    direction: "above",
    claim: "Tests: the rung-less ladder",
    sourceLabel: "NY Fed labor market",
    sourceUrl: "https://www.newyorkfed.org/research/college-labor-market",
  },
  {
    id: "info_employment",
    label: "TECH / INFO EMPLOYMENT",
    sub: "Information sector, CES (thousands) · ref: LLM-era ATH ~3,080k (2022)",
    unit: "k",
    decimals: 0,
    baseline: 2960,
    watch: 2926,
    doom: 2772,
    direction: "below",
    claim: "Tests: programmers → weavers",
    sourceLabel: "FRED USINFO",
    sourceUrl: "https://fred.stlouisfed.org/series/USINFO",
  },
  {
    id: "biz_applications",
    label: "New Business Applications",
    sub: "High-propensity applications, monthly",
    unit: "k",
    decimals: 0,
    baseline: 140,
    watch: 120,
    doom: 100,
    direction: "below",
    claim: "Tests: reabsorption engine",
    sourceLabel: "Census BFS",
    sourceUrl: "https://www.census.gov/econ/bfs/",
  },
  {
    id: "median_income",
    label: "Real Median HH Income",
    sub: "Annual real median household income",
    unit: "$k",
    decimals: 1,
    baseline: 83.7,
    watch: 82.0,
    doom: 79.5,
    direction: "below",
    claim: "Tests: Engels' pause detector",
    sourceLabel: "FRED MEHOINUSA672N",
    sourceUrl: "https://fred.stlouisfed.org/series/MEHOINUSA672N",
  },
];

const HELP_GLOBAL = "All DOOM lines assume the reading is SUSTAINED (2+ quarters) and occurs OUTSIDE an "
  + "NBER-dated recession. Recessions trip these numbers for boring cyclical reasons; the doom claim is "
  + "structural displacement, so cyclical hits don't count. Thresholds were frozen July 2026. Amendments "
  + "require unanimous lunch-table consent and leave a git commit.";

const HELP_TEXT = {
  prime_age_epop: "Cleanest 'are humans employed' number — immune to retirement demographics and "
    + "discouraged-worker definition fights. WATCH 80.0 = below the recent plateau. DOOM 78.7 = a "
    + "2-point decline, which in the modern record has ONLY ever happened inside recessions "
    + "(2008: ~5pts, COVID: ~10pts). A 2-point drift during an expansion would be genuinely "
    + "unprecedented.",
  u3: "Displacement speedometer. WATCH 5.0 = leaving the full-employment zone (near historical NAIRU "
    + "estimates). DOOM 6.0 = a level reached in every ordinary recession — which is exactly why the "
    + "no-recession qualifier above is load-bearing. 6% sustained in an expansion, with duration also "
    + "rising, is the structural signature.",
  unemp_duration: "The reabsorption gauge — tells you whether displaced people find NEW work, which is "
    + "the whole doomer mechanism. Healthy-market norm ~10 wks. WATCH 13 = post-recession lingering. "
    + "DOOM 17 = territory seen only in the aftermath of 2008 and COVID. Rate flat + duration exploding "
    + "= people falling out, not moving over.",
  labor_share: "The 'rich eat the gains' claim in one number: share of output paid to labor vs. capital. "
    + "WATCH 54.5 = resuming the secular slide. DOOM 52.8 = a 3-point drop, compressing the epochal "
    + "2000–2015 decline (~5pts over 15 years) into a few — the Engels' pause pattern: productivity up, "
    + "workers' slice shrinking.",
  grad_unemp: "The strongest doomer claim, isolated: the entry rung. NY Fed series, college grads 22–27. "
    + "WATCH 6.5 = clearly elevated vs. norms. DOOM 8.5 = near the series' 2010–11 record, sustained "
    + "without a recession to blame. NOTE: 2025 prints ran hot — verify baseline on first scan before "
    + "signing predictions.",
  grad_underemp: "Catches the failure mode unemployment misses: grads 'employed' but rung-less — the CS "
    + "degree behind the espresso machine. The series has oscillated 38–44% for its ENTIRE published "
    + "history. WATCH 45 breaks the band; DOOM 50 has never been observed. This threshold pair is the "
    + "best-grounded on the board.",
  info_employment: "Proxy for 'programmers become weavers.' Anchored to the LLM-era peak (~3,080k, "
    + "mid-2022), NOT the literal ATH — the sector's true peak was dot-com 2001 (~3.7M) and it never "
    + "recovered, so a literal-ATH rule reads DOOM on day one. WATCH = -5% from era peak; DOOM = -10%. "
    + "Caveat: NAICS 51 bundles telecom, publishing, and streaming with tech — a blunt instrument, chosen "
    + "because it prints monthly.",
  biz_applications: "The reabsorption engine, forward-looking: if AI is a multiplier, cheap cognition "
    + "should show up as company formation. Post-2020 norm ~140k/mo (high-propensity). WATCH 120 = surge "
    + "fully faded. DOOM 100 = below the pre-pandemic norm DESPITE everyone allegedly wielding a free "
    + "software team — multiplier thesis failing.",
  median_income: "The blunt backstop: is the median household actually getting poorer? Annual, Sept "
    + "release, so this card lags. WATCH 82.0 = one real decline. DOOM 79.5 = consecutive declines "
    + "totaling ~5% — outside recessions, this has never happened in the postwar record. Every doom "
    + "scenario requires it; no tech wave has produced it.",
};

const blink = keyframes`
  0%, 49% { opacity: 1; }
  50%, 100% { opacity: 0; }
`;

const Page = styled.main`
  min-height: 100vh;
  background:
    repeating-linear-gradient(0deg, rgba(0, 0, 0, 0.18) 0, rgba(0, 0, 0, 0.18) 1px, transparent 1px, transparent 4px),
    radial-gradient(circle at 18% 0%, rgba(255, 59, 48, 0.18), transparent 30%),
    linear-gradient(180deg, #0f0b09 0%, ${C.bg} 58%, #080706 100%);
  color: ${C.text};
  font-family: ${MONO};
`;

const Shell = styled.div`
  width: min(1120px, calc(100% - 32px));
  margin: 0 auto;
  padding: 28px 0 36px;
`;

const Panel = styled.section`
  background: ${(props) => (props.$highlight ? C.panelHi : C.panel)};
  border: 2px solid ${(props) => (props.$alert ? C.redDeep : C.border)};
  box-shadow: 0 0 28px rgba(255, 59, 48, 0.08);
`;

const HeaderPanel = styled(Panel)`
  padding: 20px;
  margin-bottom: 14px;
`;

const TitleRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 8px 16px;
`;

const Title = styled.h1`
  margin: 0;
  color: ${C.red};
  font-family: ${MONO};
  font-size: clamp(2.35rem, 9vw, 4.5rem);
  line-height: 0.9;
  letter-spacing: 0.16em;
  text-shadow: 0 0 14px ${C.redDeep}, 0 0 2px ${C.red};
`;

const Subtitle = styled.div`
  color: ${C.dim};
  font-size: 0.75rem;
  letter-spacing: 0.16em;
  text-transform: uppercase;
`;

const TerminalLine = styled.div`
  margin-top: 14px;
  color: ${C.amber};
  font-size: 0.8rem;
  line-height: 1.6;
`;

const Cursor = styled.span`
  animation: ${blink} 1.1s step-end infinite;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

const MeterPanel = styled(Panel)`
  padding: 18px;
  margin-bottom: 14px;
`;

const MeterTop = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  margin-bottom: 14px;
`;

const Kicker = styled.div`
  color: ${C.dim};
  font-size: 0.72rem;
  letter-spacing: 0.16em;
  text-transform: uppercase;
`;

const StatusText = styled(Kicker)`
  color: ${(props) => props.$color};
`;

const MeterBody = styled.div`
  display: grid;
  grid-template-columns: auto minmax(0, 1fr);
  gap: 18px;
  align-items: center;

  @media (max-width: 620px) {
    grid-template-columns: 1fr;
  }
`;

const Health = styled.div`
  color: ${(props) => props.$color};
  font-size: clamp(2.8rem, 12vw, 5rem);
  font-weight: 800;
  line-height: 0.9;
  text-shadow: 0 0 10px ${(props) => props.$color}55;
  font-variant-numeric: tabular-nums;
`;

const SegmentBar = styled.div`
  display: grid;
  grid-template-columns: repeat(20, 1fr);
  gap: 4px;
  min-height: 38px;
`;

const Segment = styled.div`
  background: ${(props) => (props.$active ? props.$color : "#241d14")};
  box-shadow: ${(props) => (props.$active ? `0 0 8px ${props.$color}77` : "none")};
`;

const MetaRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-top: 16px;
  color: ${C.dim};
  font-size: 0.72rem;
  line-height: 1.5;
`;

const GlobalHelp = styled.div`
  margin-top: 14px;
  padding-top: 12px;
  border-top: 1px solid ${C.border};
  color: ${C.dim};
  font-size: 0.68rem;
  line-height: 1.55;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 12px;

  @media (max-width: 980px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const Card = styled.article`
  display: flex;
  flex-direction: column;
  min-height: 196px;
  padding: 14px;
  background: ${C.panel};
  border: 2px solid ${(props) => props.$accent || C.border};
`;

const CardTop = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 10px;
`;

const CardTitle = styled.h2`
  margin: 0;
  color: ${C.text};
  font-family: ${MONO};
  font-size: 0.78rem;
  line-height: 1.35;
  letter-spacing: 0.1em;
  text-transform: uppercase;
`;

const TitleHelp = styled.span`
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
  width: 18px;
  height: 18px;
  margin-top: -1px;
  color: ${C.dim};
  cursor: help;

  svg {
    width: 12px;
    height: 12px;
  }

  &::after {
    content: attr(data-help);
    position: absolute;
    left: 50%;
    top: calc(100% + 10px);
    z-index: 20;
    width: min(320px, calc(100vw - 48px));
    padding: 10px;
    border: 1px solid ${C.border};
    background: ${C.panelHi};
    color: ${C.text};
    box-shadow: 0 12px 32px rgba(0, 0, 0, 0.35);
    font-size: 0.68rem;
    font-weight: 400;
    letter-spacing: 0;
    line-height: 1.55;
    text-transform: none;
    transform: translateX(-50%) translateY(-4px);
    opacity: 0;
    pointer-events: none;
    transition:
      opacity 0.15s ease,
      transform 0.15s ease;
  }

  &:hover,
  &:focus {
    color: ${C.amber};
    outline: none;
  }

  &:hover::after,
  &:focus::after {
    opacity: 1;
    transform: translateX(-50%) translateY(0);
  }
`;

const Badge = styled.div`
  flex: 0 0 auto;
  border: 1px solid ${(props) => props.$color};
  color: ${(props) => props.$color};
  padding: 3px 7px;
  font-size: 0.62rem;
  font-weight: 800;
  letter-spacing: 0.12em;
`;

const ValueRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 8px;
  margin: 13px 0 8px;
`;

const Value = styled.div`
  color: ${(props) => props.$color};
  font-size: 2rem;
  line-height: 1;
  font-weight: 800;
  font-variant-numeric: tabular-nums;
`;

const AsOf = styled.div`
  color: ${C.dim};
  font-size: 0.65rem;
  line-height: 1.4;
`;

const Small = styled.div`
  color: ${C.dim};
  font-size: 0.68rem;
  line-height: 1.55;
`;

const SparkWrap = styled.div`
  margin: 10px 0 12px;
`;

const SparkMeta = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 5px;
  color: ${C.dim};
  font-size: 0.58rem;
  letter-spacing: 0.08em;
  text-transform: uppercase;
`;

const SparkSvg = styled.svg`
  display: block;
  width: 100%;
  height: 38px;
  overflow: visible;
`;

const Thresholds = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px 12px;
  margin-top: auto;
  padding-top: 10px;
  border-top: 1px solid ${C.border};
  color: ${C.dim};
  font-size: 0.64rem;
`;

const SourceLink = styled.a`
  color: ${C.dim};
  text-decoration: underline;
  text-decoration-color: rgba(132, 123, 108, 0.45);
  text-underline-offset: 3px;

  &:hover {
    color: ${C.amber};
  }
`;

const FooterPanel = styled(Panel)`
  margin-top: 14px;
  padding: 12px 14px;
  color: ${C.dim};
  font-size: 0.68rem;
  line-height: 1.6;
`;

function statStatus(stat, value) {
  if (value == null || Number.isNaN(value)) return "unknown";
  if (stat.direction === "below") {
    if (value <= stat.doom) return "doom";
    if (value <= stat.watch) return "watch";
    return "fine";
  }
  if (value >= stat.doom) return "doom";
  if (value >= stat.watch) return "watch";
  return "fine";
}

const STATUS_STYLE = {
  fine: { color: C.green, label: "Fine" },
  watch: { color: C.amber, label: "Watch" },
  doom: { color: C.red, label: "Doom" },
  unknown: { color: C.dim, label: "No Data" },
};

function moodFor(health) {
  if (health >= 85) return { text: "All Quiet", color: C.green };
  if (health >= 65) return { text: "Side-Eye", color: C.green };
  if (health >= 45) return { text: "Rattled", color: C.amber };
  if (health >= 25) return { text: "Defcon Lunch", color: C.amber };
  return { text: "Told You So", color: C.red };
}

function formatValue(stat, value) {
  const fixed = Number(value).toFixed(stat.decimals);
  if (stat.unit === "$k") return `$${fixed}k`;
  if (stat.unit === "k") return `${fixed.replace(/\.0$/, "")}k`;
  return `${fixed}${stat.unit}`;
}

function Sparkline({ color, history }) {
  const points = (history || []).filter((point) => (
    point?.date && typeof point.value === "number" && Number.isFinite(point.value)
  ));

  if (points.length < 2) {
    return (
      <SparkWrap>
        <SparkMeta>
          <span>Trend</span>
          <span>Pending</span>
        </SparkMeta>
      </SparkWrap>
    );
  }

  const values = points.map((point) => point.value);
  const min = Math.min(...values);
  const max = Math.max(...values);
  const spread = max - min || 1;
  const width = 180;
  const height = 38;
  const pathPoints = points.map((point, index) => {
    const x = (index / (points.length - 1)) * width;
    const y = height - ((point.value - min) / spread) * (height - 4) - 2;
    return `${x.toFixed(2)},${y.toFixed(2)}`;
  }).join(" ");
  const first = points[0];
  const last = points[points.length - 1];

  return (
    <SparkWrap>
      <SparkMeta>
        <span>Trend since Jan 2022</span>
        <span>{first.date.slice(0, 7)} → {last.date.slice(0, 7)}</span>
      </SparkMeta>
      <SparkSvg viewBox={`0 0 ${width} ${height}`} preserveAspectRatio="none" aria-hidden="true">
        <line x1="0" y1={height - 1} x2={width} y2={height - 1} stroke={C.border} strokeWidth="1" />
        <polyline points={pathPoints} fill="none" stroke={color} strokeWidth="2.4" vectorEffect="non-scaling-stroke" />
      </SparkSvg>
    </SparkWrap>
  );
}

function buildHeadline(statuses) {
  const doomCount = statuses.filter((item) => item.status === "doom").length;
  const watchCount = statuses.filter((item) => item.status === "watch").length;
  const liveCount = statuses.filter((item) => !item.usingBaseline).length;

  if (doomCount > 0) return `${doomCount} panel${doomCount === 1 ? "" : "s"} in doom. Cancel the victory lap, schedule the argument.`;
  if (watchCount > 0) return `${watchCount} yellow light${watchCount === 1 ? "" : "s"}. The machines have not won; they are loitering.`;
  if (liveCount > 0) return "Labor market still exists. Doomers advised to update priors, quietly.";
  return "Baseline mode. The apocalypse declined to provide structured data.";
}

export default function DoomDashboard() {
  const [scan, setScan] = useState(null);
  const [error, setError] = useState(null);

  const runScan = useCallback(async (fallback = null) => {
    setError(null);

    try {
      const response = await fetch(`/doom-data.json?ts=${Date.now()}`, {
        cache: "no-store",
      });

      if (!response.ok) {
        throw new Error(`doom-data.json returned HTTP ${response.status}`);
      }

      const data = await response.json();
      const result = {
        values: { ...(fallback?.values || {}), ...(data.values || {}) },
        timestamp: Date.now(),
        updatedAt: data.updatedAt,
        failures: data.failures || [],
      };

      setScan(result);
      localStorage.setItem(CACHE_KEY, JSON.stringify(result));

      if (result.failures.length > 0) {
        setError(`Static data refreshed with ${result.failures.length} source warning(s).`);
      }
    } catch (err) {
      setError("Could not load doom-data.json; showing cached/baseline values.");
      if (fallback) setScan(fallback);
    }
  }, []);

  useEffect(() => {
    const cached = localStorage.getItem(CACHE_KEY);
    const parsed = cached ? JSON.parse(cached) : null;
    if (parsed) setScan(parsed);

    runScan(parsed);
  }, [runScan]);

  const statuses = useMemo(() => STATS.map((stat) => {
    const live = scan?.values?.[stat.id];
    const value = typeof live?.value === "number" ? live.value : stat.baseline;
    const status = statStatus(stat, value);
    return {
      stat,
      value,
      asOf: live?.asOf,
      sourceLabel: live?.sourceLabel || stat.sourceLabel,
      sourceUrl: live?.sourceUrl || stat.sourceUrl,
      history: live?.history || [],
      status,
      usingBaseline: typeof live?.value !== "number",
    };
  }), [scan]);

  const doomPoints = statuses.reduce((acc, item) => (
    acc + (item.status === "doom" ? 2 : item.status === "watch" ? 1 : 0)
  ), 0);
  const health = Math.round(100 - (doomPoints / (statuses.length * 2)) * 100);
  const mood = moodFor(health);
  const litSegments = Math.round((health / 100) * 20);
  const segmentColor = health >= 65 ? C.green : health >= 35 ? C.amber : C.red;
  const headline = buildHeadline(statuses);
  const lastScanStr = scan?.updatedAt
    ? new Date(scan.updatedAt).toLocaleString()
    : scan ? new Date(scan.timestamp).toLocaleString() : "Never";

  return (
    <Page>
      <Shell>
        <HeaderPanel $alert>
          <TitleRow>
            <Title>D.O.O.M.</Title>
            <Subtitle>Dashboard Of Occupational Metrics · Est. Lunch, July 2026</Subtitle>
          </TitleRow>
          <TerminalLine>
            &gt;&gt; {headline}
            <Cursor>_</Cursor>
          </TerminalLine>
        </HeaderPanel>

        <MeterPanel $highlight>
          <MeterTop>
            <Kicker>Civilization Health</Kicker>
            <StatusText $color={mood.color}>Status: {mood.text}</StatusText>
          </MeterTop>
          <MeterBody>
            <Health $color={mood.color}>{health}%</Health>
            <SegmentBar aria-label={`Civilization health ${health} percent`}>
              {Array.from({ length: 20 }).map((_, index) => (
                <Segment
                  // eslint-disable-next-line react/no-array-index-key
                  key={index}
                  $active={index < litSegments}
                  $color={segmentColor}
                />
              ))}
            </SegmentBar>
          </MeterBody>
          <MetaRow>
            <div>
              Last scan: {lastScanStr}
              {error ? <span style={{ color: C.red }}> · {error}</span> : null}
            </div>
          </MetaRow>
          <GlobalHelp>{HELP_GLOBAL}</GlobalHelp>
        </MeterPanel>

        <Grid>
          {statuses.map(({ stat, value, asOf, sourceLabel, sourceUrl, history, status, usingBaseline }) => {
            const style = STATUS_STYLE[status];
            const TrendIcon = stat.direction === "below" ? TrendingDown : TrendingUp;
            return (
              <Card key={stat.id}>
                <CardTop>
                  <CardTitle>
                    {stat.label}
                    {" "}
                    <TitleHelp
                      aria-label={`Help for ${stat.label}`}
                      data-help={HELP_TEXT[stat.id]}
                      tabIndex="0"
                    >
                      <CircleHelp aria-hidden="true" />
                    </TitleHelp>
                  </CardTitle>
                  <Badge $color={style.color}>{style.label}</Badge>
                </CardTop>
                <ValueRow>
                  <Value $color={style.color}>{formatValue(stat, value)}</Value>
                  <AsOf>{usingBaseline ? "Baseline Jul 2026" : `As of ${asOf}`}</AsOf>
                </ValueRow>
                <Small>{stat.sub}</Small>
                <Small>
                  <SourceLink href={sourceUrl} target="_blank" rel="noopener noreferrer">
                    {sourceLabel}
                  </SourceLink>
                </Small>
                <Sparkline color={style.color} history={history} />
                <Thresholds>
                  <span>Base {stat.baseline}</span>
                  <span style={{ color: C.amber }}>
                    Watch {stat.direction === "below" ? "<=" : ">="}
                    {stat.watch}
                  </span>
                  <span style={{ color: C.red }}>
                    Doom {stat.direction === "below" ? "<=" : ">="}
                    {stat.doom}
                  </span>
                </Thresholds>
                <Small style={{ marginTop: 8, fontStyle: "italic" }}>
                  <TrendIcon size={13} aria-hidden="true" /> {stat.claim}
                </Small>
              </Card>
            );
          })}

        </Grid>

        <FooterPanel>
          <span style={{ color: C.red }}>Rules of engagement:</span> predictions on record before scanning. Data comes
          from <SourceLink href="/doom-data.json">doom-data.json</SourceLink>, which can be refreshed by the
          dependency-free updater script before deploy. Goalpost relocation triggers audible klaxon.
        </FooterPanel>
      </Shell>
    </Page>
  );
}
